<template>
  <div class="checkout-page" v-loading="loading">
    <div class="checkout-page__inner">
      <el-alert v-if="error" :closable="false" type="warning" show-icon class="checkout-alert">
        {{ error }}
      </el-alert>

      <div v-if="order" class="checkout-layout">
        <section class="checkout-main">
          <article class="checkout-card checkout-card--summary">
            <p class="checkout-card__eyebrow">推广订单支付</p>
            <h1 class="checkout-card__title">{{ order.campaign_name || '未命名推广计划' }}</h1>
            <p class="checkout-card__desc">
              请选择支付方式并发起支付。扫码支付会展示二维码，网银支付会打开汇付收银页面，本页面会持续查询订单状态。
            </p>

            <div class="summary-grid">
              <div class="summary-item">
                <span>商户 ID</span>
                <strong>{{ order.commercial_id || '-' }}</strong>
              </div>
              <div class="summary-item">
                <span>订单号</span>
                <strong>{{ order.order_no || '-' }}</strong>
              </div>
              <div class="summary-item">
                <span>支付金额</span>
                <strong class="summary-item__amount">{{ formatMoney(order.settlement_amount, order.settlement_currency)
                  }}</strong>
              </div>
              <div class="summary-item">
                <span>状态</span>
                <strong>{{ statusLabel(order.status) }}</strong>
              </div>
            </div>
          </article>

          <article class="checkout-card">
            <div class="section-head">
              <div>
                <p class="section-head__eyebrow">支付方式</p>
                <h2>选择支付通道</h2>
              </div>
              <span class="section-head__badge">扫码 / 跳转支付</span>
            </div>

            <div class="channel-grid">
              <button v-for="channel in availableChannels" :key="channel.code" type="button" class="channel-card"
                :class="{ 'is-active': selectedChannel === channel.code, 'is-disabled': !channel.enabled }"
                :disabled="!channel.enabled" @click="selectedChannel = channel.code">
                <span class="channel-card__radio"></span>
                <img :src="channel.icon" class="channel-card__logo">
                <span class="channel-card__text">
                  <strong>{{ channel.label }}</strong>
                  <!-- <small>{{ channelHint(channel.code) }}</small> -->
                </span>
              </button>
            </div>

            <label class="agreement-box">
              <input v-model="agreed" type="checkbox" />
              <span>我确认本次支付用于当前推广订单，并已获得授权。</span>
            </label>

            <div class="checkout-actions">
              <el-button type="primary" class="checkout-pay-button" :loading="submitting" :disabled="!canSubmit"
                @click="startPaymentFlow">
                {{ primaryActionText }}
              </el-button>
              <el-button v-if="showQrButton" :disabled="submitting" @click="openQrDialog">
                查看二维码
              </el-button>
              <el-button v-if="canCancel" :loading="cancelling" @click="cancelOrder">
                取消订单
              </el-button>
              <el-button v-if="canRecreate" :loading="recreating" @click="recreateOrder">
                重新创建订单
              </el-button>
            </div>
          </article>
        </section>

        <aside class="checkout-side">
          <article class="checkout-card">
            <div class="section-head">
              <div>
                <p class="section-head__eyebrow">结算信息</p>
                <h2>订单详情</h2>
              </div>
            </div>

            <div class="detail-list">
              <div class="detail-row">
                <span>汇率</span>
                <strong>1 {{ order.fx_base_currency }} = {{ Number(order.fx_rate || 0).toFixed(4) }} {{
                  order.fx_quote_currency }}</strong>
              </div>
              <div class="detail-row">
                <span>汇率来源</span>
                <strong>{{ order.fx_provider || '-' }}</strong>
                <small>{{ formatDateTime(order.fx_fetched_at) }}</small>
              </div>
              <div class="detail-row">
                <span>原始预算</span>
                <strong>{{ formatMoney(order.original_amount, order.original_currency) }}</strong>
              </div>
              <div class="detail-row" v-if="order.expire_at">
                <span>过期时间</span>
                <strong>{{ formatDateTime(order.expire_at) }}</strong>
              </div>
              <div class="detail-row" v-if="order.paid_at">
                <span>支付时间</span>
                <strong>{{ formatDateTime(order.paid_at) }}</strong>
              </div>
            </div>
          </article>

          <article class="checkout-card">
            <div class="section-head">
              <div>
                <p class="section-head__eyebrow">支付状态</p>
                <h2>自动查询</h2>
              </div>
            </div>
            <p class="status-copy">{{ statusDescription }}</p>
            <el-button v-if="order && ['pending', 'processing'].includes(order.status)" type="text"
              @click="queryStatus(false)">
              立即刷新
            </el-button>
          </article>
        </aside>
      </div>
    </div>

    <el-dialog :visible.sync="dialogVisible" width="420px" custom-class="checkout-qr-dialog"
      :close-on-click-modal="false" append-to-body>
      <div class="qr-dialog">
        <div class="qr-dialog__logo">
          <img :src="selectedChannelMeta.icon" />
        </div>
        <h3>{{ selectedChannelMeta.label || '支付' }}</h3>
        <p>{{ dialogDescription }}</p>

        <div v-if="displayQrImage" class="qr-dialog__image">
          <img :src="displayQrImage" alt="支付二维码" />
        </div>
        <div v-else class="qr-dialog__placeholder">
          二维码暂未生成。如页面下方有支付链接，也可以直接打开完成支付。
        </div>

        <div class="qr-dialog__meta">
          <span>状态：{{ statusLabel(order && order.status) }}</span>
          <span v-if="order && order.expire_at">过期时间：{{ formatDateTime(order.expire_at) }}</span>
        </div>

        <div v-if="order && order.pay_url" class="qr-dialog__link">
          <el-input :value="order.pay_url" readonly />
        </div>

        <div class="qr-dialog__actions">
          <el-button v-if="order && order.pay_url" @click="openPayPage">
            打开支付链接
          </el-button>
          <el-button type="primary" @click="dialogVisible = false">
            关闭
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import {
  cancelPromotionPayment,
  createPromotionPaymentOrder,
  createPromotionHostedCashierIntent,
  getPromotionPaymentOrder,
  queryPromotionPaymentStatus,
  startPromotionPayment,
} from '@/api'
import {
  findPromotionPaymentChannel,
  PROMOTION_PAYMENT_CHANNELS,
  promotionPaymentStatusLabel,
} from '@/constants/promotion-payment'

function normalizeOrder(raw = {}) {
  const available = Array.isArray(raw.available_channels) && raw.available_channels.length
    ? raw.available_channels
    : PROMOTION_PAYMENT_CHANNELS
  const merged = [...available]
  const bankpay = findPromotionPaymentChannel('bankpay')
  if (bankpay && !merged.some(item => item.code === 'bankpay')) {
    merged.push(bankpay)
  }

  return {
    ...raw,
    available_channels: merged.map((item) => {
      const fallback = findPromotionPaymentChannel(item.code) || {}
      return {
        ...fallback,
        ...item,
        icon: item.icon || fallback.icon || 'PAY',
        label: item.label || fallback.label || item.code,
        enabled: item.enabled !== false,
      }
    }),
  }
}

export default {
  name: 'PromotionCheckoutPage',
  data() {
    return {
      loading: false,
      submitting: false,
      cancelling: false,
      recreating: false,
      dialogVisible: false,
      error: '',
      order: null,
      renderedQrCode: '',
      selectedChannel: '',
      agreed: true,
      pollTimer: null,
      hasHandledPaid: false,
    }
  },
  computed: {
    orderId() {
      return Number(this.$route.query.orderId || 0)
    },
    campaignId() {
      return Number(this.$route.query.campaignId || this.order?.campaign_id || 0)
    },
    availableChannels() {
      return Array.isArray(this.order?.available_channels) ? this.order.available_channels : []
    },
    selectedChannelMeta() {
      return this.availableChannels.find(item => item.code === this.selectedChannel)
        || findPromotionPaymentChannel(this.selectedChannel)
        || {}
    },
    selectedChannelMode() {
      return 1
    },
    displayQrImage() {
      return this.renderedQrCode || this.order?.qr_code_data_url || ''
    },
    currentPayAction() {
      return this.order?.pay_action || ''
    },
    isHostedCashierMode() {
      return this.selectedChannel === 'bankpay'
    },
    dialogDescription() {
      if (this.currentPayAction === 'redirect') {
        return '请在新打开的网银页面完成支付，并保持本页面打开以便自动更新状态。'
      }
      return '请使用所选支付 App 扫描二维码完成支付，并保持本页面打开以便自动更新状态。'
    },
    canSubmit() {
      return !!this.order && !!this.selectedChannel && this.agreed && this.order.status !== 'paid'
    },
    canCancel() {
      return !!this.order && !['paid', 'cancelled', 'expired', 'failed'].includes(this.order.status)
    },
    canRecreate() {
      return !!this.order && ['failed', 'cancelled', 'expired'].includes(this.order.status)
    },
    showQrButton() {
      return !!this.order
        && ['pending', 'processing'].includes(this.order.status)
        && this.currentPayAction === 'qr'
        && !!(this.order.qr_code_url || this.order.qr_code_data_url)
    },
    primaryActionText() {
      if (!this.order) return '立即支付'
      if (this.order.status === 'paid') return '已支付'
      if (this.currentPayAction === 'redirect' && this.order.pay_url && ['pending', 'processing'].includes(this.order.status)) {
        return '打开网银支付'
      }
      if (this.currentPayAction === 'qr' && (this.order.qr_code_url || this.order.qr_code_data_url) && ['pending', 'processing'].includes(this.order.status)) {
        return '查看支付二维码'
      }
      if (this.currentPayAction === 'pending_frontend') return '查看支付信息'
      return '立即支付'
    },
    statusDescription() {
      const mapping = {
        created: '订单已创建，请选择支付方式后继续。',
        pending: '支付已发起，本页面正在自动查询支付状态。',
        processing: '上游通道正在确认支付结果，请保持页面打开。',
        paid: '支付已完成，推广计划会自动启用。',
        failed: '支付失败，可以切换支付方式或重新创建订单。',
        cancelled: '订单已取消，可以重新创建订单后继续。',
        expired: '订单已过期，可以重新创建订单后继续。',
      }
      return mapping[this.order?.status] || '请先刷新订单状态后再继续操作。'
    },
  },
  mounted() {
    this.initPage()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  watch: {
    '$route.query.orderId': {
      handler() {
        this.initPage()
      },
    },
    'order.qr_code_url': {
      handler() {
        this.updateRenderedQrCode()
      },
    },
  },
  methods: {
    statusLabel(status) {
      return promotionPaymentStatusLabel(status)
    },
    channelHint(code) {
      const hints = {
        alipay: '使用支付宝扫码支付',
        unionpay: '使用云闪付扫码支付',
        bankpay: '跳转至网银页面支付',
      }
      return hints[code] || '在线支付'
    },
    formatMoney(amount, currency) {
      const numeric = Number(amount || 0)
      const code = String(currency || 'USD').toUpperCase()
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: code,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numeric)
      } catch (_) {
        return `${code} ${numeric.toFixed(2)}`
      }
    },
    formatDateTime(value) {
      if (!value) return '--'
      const date = new Date(Number(value))
      if (Number.isNaN(date.getTime())) return String(value)
      const pad = num => String(num).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    async initPage() {
      this.error = ''
      this.dialogVisible = false
      this.hasHandledPaid = false
      if (!this.orderId) {
        this.order = null
        this.stopPolling()
        this.error = '缺少 orderId，无法加载支付订单。'
        return
      }
      await this.loadOrder()
    },
    async loadOrder(silent = false) {
      if (!silent) this.loading = true
      try {
        const { data } = await getPromotionPaymentOrder(this.orderId)
        this.order = normalizeOrder(data)
        await this.updateRenderedQrCode()
        if (!this.selectedChannel || !this.availableChannels.some(item => item.code === this.selectedChannel)) {
          this.selectedChannel = this.order.channel || (this.availableChannels[0] || {}).code || ''
        }
        this.syncPolling()
        this.handlePaidState()
      } catch (error) {
        this.error = error.message || '加载支付订单失败'
      } finally {
        if (!silent) this.loading = false
      }
    },
    syncPolling() {
      if (!this.order) {
        this.stopPolling()
        return
      }
      if (['pending', 'processing'].includes(this.order.status)) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
    startPolling() {
      if (this.pollTimer) return
      this.pollTimer = window.setInterval(() => {
        this.queryStatus(true)
      }, 5000)
    },
    stopPolling() {
      if (!this.pollTimer) return
      window.clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    async startPaymentFlow() {
      if (!this.order || (!this.isHostedCashierMode && !this.selectedChannel) || !this.agreed) return

      if (this.currentPayAction === 'qr' && (this.order.qr_code_url || this.order.qr_code_data_url) && ['pending', 'processing'].includes(this.order.status)) {
        this.dialogVisible = true
        return
      }
      if (this.currentPayAction === 'redirect' && this.order.pay_url && ['pending', 'processing'].includes(this.order.status)) {
        this.openPayPage()
        return
      }

      this.submitting = true
      const redirectWindow = (this.isHostedCashierMode || this.selectedChannel === 'bankpay') ? window.open('', '_blank') : null
      try {
        const { data } = this.isHostedCashierMode
          ? await createPromotionHostedCashierIntent(this.order)
          : await startPromotionPayment(this.order.id, {
            channel: this.selectedChannel,
            mode: this.selectedChannelMode,
          })
        this.order = normalizeOrder({
          ...this.order,
          ...data,
        })
        await this.updateRenderedQrCode()
        this.selectedChannel = this.order.channel || this.selectedChannel
        this.syncPolling()
        if (this.order.pay_action === 'qr' && (this.order.qr_code_url || this.order.qr_code_data_url)) {
          this.dialogVisible = true
          this.$message.success('支付二维码已生成，请在 App 内完成支付。')
        } else if (this.order.pay_action === 'redirect' && this.order.pay_url) {
          this.openPayPage(redirectWindow)
          this.$message.success('请在新打开的网银页面完成支付。')
        } else {
          if (redirectWindow) redirectWindow.close()
          this.$message.warning('支付已发起，但暂未获得可展示的支付信息。')
        }
      } catch (error) {
        if (redirectWindow) redirectWindow.close()
        this.$message.error(error.message || '发起支付失败')
      } finally {
        this.submitting = false
      }
    },
    openQrDialog() {
      if (!this.order) return
      this.dialogVisible = true
    },
    async cancelOrder() {
      if (!this.order) return
      this.cancelling = true
      try {
        const { data } = await cancelPromotionPayment(this.order.id)
        this.order = normalizeOrder(data)
        this.renderedQrCode = ''
        this.dialogVisible = false
        this.syncPolling()
        this.$message.success('订单已取消')
      } catch (error) {
        this.$message.error(error.message || '取消订单失败')
      } finally {
        this.cancelling = false
      }
    },
    async recreateOrder() {
      if (!this.order) return
      this.recreating = true
      try {
        const { data } = await createPromotionPaymentOrder(this.order.campaign_id, this.order.commercial_id)
        this.stopPolling()
        this.dialogVisible = false
        this.$router.replace({
          path: '/promotion/checkout',
          query: {
            campaignId: this.order.campaign_id,
            orderId: data.id,
          },
        })
      } catch (error) {
        this.$message.error(error.message || '重新创建订单失败')
      } finally {
        this.recreating = false
      }
    },
    async queryStatus(silent = false) {
      if (!this.order) return
      try {
        const { data } = await queryPromotionPaymentStatus(this.order.id)
        this.order = normalizeOrder(data)
        await this.updateRenderedQrCode()
        this.syncPolling()
        this.handlePaidState()
      } catch (error) {
        if (!silent) {
          this.$message.error(error.message || '查询支付状态失败')
        }
      }
    },
    handlePaidState() {
      if (!this.order || this.order.status !== 'paid' || this.hasHandledPaid) return
      this.hasHandledPaid = true
      this.stopPolling()
      this.dialogVisible = false
      this.$message.success('支付成功，正在返回推广页面...')
      window.setTimeout(() => {
        this.$router.replace({
          path: '/promotion',
          query: {
            campaignId: this.campaignId || this.order.campaign_id,
            orderId: this.order.id,
            paymentStatus: 'paid',
          },
        })
      }, 1200)
    },
    openPayPage(targetWindow) {
      if (!this.order?.pay_url) return
      if (targetWindow && !targetWindow.closed) {
        targetWindow.location.href = this.order.pay_url
        return
      }
      window.open(this.order.pay_url, '_blank')
    },
    async updateRenderedQrCode() {
      const qrText = this.order?.qr_code_url
      if (!qrText) {
        this.renderedQrCode = ''
        return
      }
      try {
        this.renderedQrCode = await QRCode.toDataURL(qrText, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 280,
        })
      } catch (error) {
        this.renderedQrCode = ''
        console.error('[promotion-checkout] QR render failed:', error)
      }
    },
  },
}
</script>

<style scoped>
.checkout-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 30%),
    radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 26%),
    linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
  padding: 32px 16px;
}

.checkout-page__inner {
  max-width: 1180px;
  margin: 0 auto;
}

.checkout-alert {
  margin-bottom: 18px;
}

.checkout-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
}

.checkout-main,
.checkout-side {
  min-width: 0;
}

.checkout-side {
  display: grid;
  gap: 18px;
}

.checkout-card {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #d9e3f3;
  border-radius: 28px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.08);
  padding: 24px;
}

.checkout-card+.checkout-card {
  margin-top: 20px;
}

.checkout-card__eyebrow,
.section-head__eyebrow {
  margin: 0;
  color: #3158d6;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.checkout-card__title {
  margin: 10px 0 0;
  color: #0f172a;
  font-size: 34px;
  line-height: 1.1;
  font-weight: 900;
}

.checkout-card__desc,
.status-copy {
  margin: 14px 0 0;
  color: #5b6b83;
  font-size: 14px;
  line-height: 1.8;
}

.summary-grid {
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.summary-item {
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8fbff 0%, #f1f6ff 100%);
  border: 1px solid #e0e8f5;
}

.summary-item span,
.detail-row span {
  display: block;
  color: #68768b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.summary-item strong,
.detail-row strong {
  display: block;
  margin-top: 8px;
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
}

.summary-item__amount {
  color: #c81e4b;
  font-size: 30px;
  line-height: 1.1;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.section-head h2 {
  margin: 8px 0 0;
  color: #0f172a;
  font-size: 22px;
  font-weight: 900;
}

.section-head__badge {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(49, 88, 214, 0.1);
  color: #3158d6;
  font-size: 12px;
  font-weight: 800;
}

.channel-grid {
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.hosted-cashier-panel {
  margin-top: 20px;
  padding: 18px 20px;
  border: 1px solid #c8d7f2;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%);
  display: grid;
  gap: 8px;
}

.hosted-cashier-panel strong {
  color: #0f172a;
  font-size: 18px;
  font-weight: 900;
}

.hosted-cashier-panel span {
  color: #5b6b83;
  font-size: 14px;
  line-height: 1.7;
}

.channel-card {
  padding: 16px;
  border: 1px solid #d5deeb;
  border-radius: 20px;
  background: #fff;
  display: grid;
  grid-template-columns: 20px 42px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.channel-card:hover {
  transform: translateY(-1px);
  border-color: #9bb4eb;
}

.channel-card.is-active {
  border-color: #3158d6;
  box-shadow: 0 14px 28px rgba(49, 88, 214, 0.16);
}

.channel-card.is-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.channel-card__radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #c9d4e4;
  position: relative;
}

.channel-card.is-active .channel-card__radio {
  border-color: #3158d6;
}

.channel-card.is-active .channel-card__radio::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #3158d6;
  transform: translate(-50%, -50%);
}

.channel-card__logo,
.qr-dialog__logo {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  font-weight: 900;
  letter-spacing: 0.04em;
}

.qr-dialog__logo img{
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  font-size: 18px;
}

.channel-card__logo.is-alipay,
.qr-dialog__logo.is-alipay {
  background: linear-gradient(180deg, #2f7cff 0%, #1656de 100%);
}

.channel-card__logo.is-unionpay,
.qr-dialog__logo.is-unionpay {
  background: linear-gradient(180deg, #254fdf 0%, #d62e3b 100%);
}

.channel-card__logo.is-default,
.qr-dialog__logo.is-default {
  background: linear-gradient(180deg, #1f2937 0%, #0f172a 100%);
}

.channel-card__text strong,
.channel-card__text small {
  display: block;
}

.channel-card__text strong {
  color: #0f172a;
  font-size: 15px;
  font-weight: 800;
}

.channel-card__text small {
  margin-top: 6px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.agreement-box {
  margin-top: 20px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #334155;
  font-size: 14px;
  line-height: 1.7;
}

.agreement-box input {
  margin-top: 4px;
}

.checkout-actions {
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.checkout-pay-button {
  min-width: 220px;
  height: 48px;
  border-radius: 999px;
  border: 0;
  background: linear-gradient(180deg, #2d6bf5 0%, #193fd8 100%);
  box-shadow: 0 16px 28px rgba(29, 78, 216, 0.22);
}

.detail-list {
  margin-top: 18px;
  display: grid;
  gap: 14px;
}

.detail-row {
  padding: 14px 0;
  border-bottom: 1px solid #e9eff8;
}

.detail-row:last-child {
  border-bottom: 0;
}

.detail-row small {
  display: block;
  margin-top: 6px;
  color: #64748b;
  font-size: 13px;
}

.qr-dialog {
  text-align: center;
}

.qr-dialog h3 {
  margin: 0;
  color: #0f172a;
  font-size: 24px;
  font-weight: 900;
}

.qr-dialog p {
  margin: 12px 0 0;
  color: #5b6b83;
  font-size: 14px;
  line-height: 1.8;
}

.qr-dialog__image,
.qr-dialog__placeholder {
  margin-top: 20px;
  border-radius: 24px;
  border: 1px solid #dce6f4;
  background: #fff;
  padding: 16px;
  min-height: 280px;
  display: grid;
  place-items: center;
}

.qr-dialog__image img {
  width: min(100%, 280px);
  height: auto;
  object-fit: contain;
}

.qr-dialog__placeholder {
  color: #94a3b8;
  font-size: 14px;
  font-weight: 700;
}

.qr-dialog__meta {
  margin-top: 16px;
  display: grid;
  gap: 6px;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
}

.qr-dialog__link {
  margin-top: 18px;
}

.qr-dialog__actions {
  margin-top: 18px;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

@media (max-width: 960px) {
  .checkout-page {
    padding: 16px;
  }

  .checkout-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .summary-grid,
  .channel-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .checkout-card {
    padding: 18px;
    border-radius: 22px;
  }

  .checkout-card__title {
    font-size: 28px;
  }

  .checkout-actions {
    position: sticky;
    bottom: 0;
    background: rgba(255, 255, 255, 0.92);
    padding-top: 12px;
  }

  .checkout-pay-button {
    width: 100%;
  }
}
</style>
